import React, { useContext } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, Avatar } from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import BottomNav from './BottomNav';
import { colors, grey, mobile, fontSize, fontWeight } from '../../styles/tokens';

// Max-width unifie pour header, contenu et bottom nav (desktop)
const MAX_WIDTH = 880;

/**
 * Configuration de la navigation Client (Mobile - Bottom Nav)
 * Navigation du plan: Accueil | Repas | Courses | Profil
 */
const clientMobileNavItems = [
  { label: 'Accueil', icon: HomeIcon, path: '/client' },
  { label: 'Repas', icon: RestaurantIcon, path: '/client/meals' },
  { label: 'Courses', icon: ShoppingCartIcon, path: '/client/shopping' },
  { label: 'Profil', icon: PersonIcon, path: '/client/profile' },
];

/**
 * Layout Client - Mobile First
 *
 * Design epure pour une utilisation smartphone:
 * - Header simple avec nom + avatar
 * - Bottom navigation 3 items
 * - Pas de sidebar
 */
const ClientLayout = () => {
  const { logout, user } = useContext(AuthContext);
  const location = useLocation();

  // Titre de page
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/client') return 'Accueil';
    if (path === '/client/meals') return 'Mes Repas';
    if (path === '/client/shopping') return 'Liste de courses';
    if (path === '/client/profile') return 'Mon Profil';
    return 'Yoco';
  };

  // Initiales pour avatar
  const getInitials = () => {
    if (!user) return '?';
    const first = user.firstName?.charAt(0) || '';
    const last = user.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: colors.bgPage }}>
      {/* Header Mobile */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: '#FFFFFF',
          borderBottom: `1px solid ${grey[200]}`,
        }}
      >
        <Box
          sx={{
            maxWidth: MAX_WIDTH,
            mx: 'auto',
            px: 2,
            py: 1.5,
            pt: `calc(${mobile.safeAreaTop} + 12px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: grey[200],
                color: grey[700],
                fontSize: fontSize.sm,
                fontWeight: fontWeight.medium,
              }}
            >
              {getInitials()}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontSize: fontSize.base,
                  fontWeight: fontWeight.semibold,
                  color: grey[900],
                  lineHeight: 1.2,
                }}
              >
                {getPageTitle()}
              </Typography>
              {user && (
                <Typography
                  sx={{
                    fontSize: fontSize.xs,
                    color: grey[500],
                    lineHeight: 1.2,
                  }}
                >
                  {user.firstName} {user.lastName}
                </Typography>
              )}
            </Box>
          </Box>

          <IconButton
            onClick={logout}
            size="small"
            sx={{
              color: grey[400],
              '&:hover': { color: grey[600], bgcolor: grey[100] },
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Contenu principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          // Centrage vertical en desktop quand contenu < hauteur
          justifyContent: { xs: 'flex-start', md: 'center' },
          // Padding bottom pour bottom nav + safe area
          pb: `calc(${mobile.bottomNavHeight}px + ${mobile.safeAreaBottom} + 16px)`,
          // Eviter debordement
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: MAX_WIDTH,
            mx: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Bottom Navigation */}
      <BottomNav navItems={clientMobileNavItems} maxWidth={MAX_WIDTH} />
    </Box>
  );
};

export default ClientLayout;
